import { Link, useNavigate } from "react-router-dom";
import { LogOut, Settings, User, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { logoutUser } from "@/store/slices/authSlice";
import type { RootState } from "@/store";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { Building2 } from "lucide-react";
import { canAccessDashboard } from "@/utils/subscription-status";

export function Navbar() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAppSelector(
    (state: RootState) => state.auth,
  );
  const dispatch = useAppDispatch();
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDark } = useTheme();
  const {
    getGlassStyles,
    getControlsGlassStyles,
    getAvatarGlassStyles,
    getDropdownGlassStyles,
  } = useTheme();
  const navigate = useNavigate();
  // Track scroll position for glass effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser());
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const getInitials = () => {
    if (!user) return "U";
    if (user.userType === "company") {
      return `${user.companyName?.charAt(0).toUpperCase() || ""}`;
    }
    if (user.userType === "individual") {
      return `${user.firstName?.charAt(0).toUpperCase() || ""}${
        user.lastName?.charAt(0).toUpperCase() || ""
      }`;
    }
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate(canAccessDashboard(user) ? "/dashboard" : "/plans");
    } else {
      navigate("/");
    }
  };

  const getFullName = () => {
    if (!user) return "";
    if (user.userType === "company") {
      return user.companyName;
    }
    return `${user.firstName} ${user.lastName}`.trim();
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 md:px-8 w-full transition-all duration-500 ease-out",
      )}
      style={getGlassStyles(isScrolled)}
    >
      <div className="flex h-16 items-center justify-between max-w-[1920px] mx-auto">
        <div className="flex items-center gap-2">
          <div
            onClick={handleLogoClick}
            className="flex items-center space-x-2 sm:space-x-3 group transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            <div className="relative flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-xl ">
              <Link to="/" className="flex items-center gap-1 sm:gap-2">
                <img
                  src={
                    isDark
                      ? "/assets/logo_full_dark.webp"
                      : "/assets/logo_full_light.webp"
                  }
                  alt="logo"
                  className="h-8 sm:h-9 md:h-10 "
                />
                {/* <span className="text-lg sm:text-xl font-medium hidden sm:block">
                  {import.meta.env.VITE_APP_NAME}
                </span> */}
              </Link>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* Language and Theme toggles with enhanced styling - Desktop only */}
          <div
            className="hidden md:flex items-center gap-2 p-1.5 rounded-full border shadow-sm"
            style={getControlsGlassStyles()}
          >
            <LanguageToggle />
            <ThemeToggle />
          </div>

          {isAuthenticated && user ? (
            <>
              {/* Desktop User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="hidden sm:flex">
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 sm:h-11 sm:w-11 rounded-full ring-2 ring-transparent hover:ring-purple-500/30 transition-all duration-300 border hover:shadow-lg hover:shadow-purple-500/10"
                    style={getAvatarGlassStyles()}
                  >
                    <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                      <AvatarImage src="" alt={t("navigation.profile")} />
                      <AvatarFallback className="bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-xs sm:text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-2.5 -right-2.5 p-1 rounded-full bg-muted border-1 border-background  flex items-center justify-center">
                      {user.userType === "company" && (
                        <Building2 className="h-3 w-3 text-muted-foreground dark:text-muted-foreground -400" />
                      )}
                      {user.userType === "individual" && (
                        <User className="h-3 w-3 text-muted-foreground dark:text-muted-foreground -400" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 sm:w-64 border shadow-2xl shadow-black/10 dark:shadow-white/5 rounded-xl mr-2 sm:mr-0"
                  align="end"
                  forceMount
                  sideOffset={8}
                  style={getDropdownGlassStyles()}
                >
                  <DropdownMenuLabel className="font-normal p-3 sm:p-4">
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                          <AvatarImage src="" alt={t("navigation.profile")} />
                          <AvatarFallback className="bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-xs sm:text-sm">
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-semibold leading-none truncate">
                            {getFullName()}
                          </p>
                          <p className="text-xs leading-none text-muted-foreground mt-1 truncate">
                            {user.email || ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />
                  {/* Desktop Theme & Language Settings */}
                  <div className="md:hidden p-2">
                    <div className="flex items-center justify-between px-2 py-1">
                      <span className="text-sm text-muted-foreground">
                        {t("dashboard.appearance")}
                      </span>
                      <div className="flex items-center gap-1">
                        <ThemeToggle />
                        <LanguageToggle />
                      </div>
                    </div>
                  </div>
                  <div className="md:hidden">
                    <DropdownMenuSeparator />
                  </div>
                  <div className="p-1">
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-950/20 focus:bg-red-50 dark:focus:bg-red-950/20 group"
                      onClick={handleLogout}
                    >
                      <div className="flex items-center justify-center h-7 w-7 sm:h-8 sm:w-8 rounded-md bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
                        <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-600 dark:text-red-400" />
                      </div>
                      <span className="font-medium text-sm">
                        {t("auth.logout")}
                      </span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Unified Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="sm:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 rounded-full border transition-all duration-200 hover:bg-accent"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-64 border shadow-2xl shadow-black/10 dark:shadow-white/5 rounded-xl mr-2"
                  align="end"
                  forceMount
                  sideOffset={8}
                  style={getDropdownGlassStyles()}
                >
                  {/* User Info Section */}
                  <DropdownMenuLabel className="font-normal p-3">
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" alt={t("navigation.profile")} />
                          <AvatarFallback className="bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-xs">
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold leading-none truncate">
                            {`${user.firstName || ""} ${user.lastName || ""}`}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {user.email || ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-green-50 dark:bg-green-950/20">
                        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-xs font-medium text-green-700 dark:text-green-400">
                          En ligne
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />

                  {/* Navigation Links */}
                  <DropdownMenuGroup className="p-1">
                    <DropdownMenuItem asChild>
                      <Link
                        to="/dashboard"
                        className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-purple-50 dark:hover:bg-purple-950/20 group"
                      >
                        <div className="flex items-center justify-center h-7 w-7 rounded-md bg-purple-100 dark:bg-purple-900/30 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                          <User className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="font-medium text-sm">Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/profile"
                        className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-950/20 group"
                      >
                        <div className="flex items-center justify-center h-7 w-7 rounded-md bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                          <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-medium text-sm">
                          {t("navigation.profile")}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/settings"
                        className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-orange-50 dark:hover:bg-orange-950/20 group"
                      >
                        <div className="flex items-center justify-center h-7 w-7 rounded-md bg-orange-100 dark:bg-orange-900/30 group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50 transition-colors">
                          <Settings className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <span className="font-medium text-sm">
                          {t("navigation.settings")}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  <DropdownMenuSeparator />

                  {/* Theme & Language Settings */}
                  <div className="p-2">
                    <div className="flex items-center justify-between px-2 py-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("dashboard.appearance")}
                      </span>
                      <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <LanguageToggle />
                      </div>
                    </div>
                  </div>

                  <DropdownMenuSeparator />

                  {/* Logout */}
                  <div className="p-1">
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-950/20 focus:bg-red-50 dark:focus:bg-red-950/20 group"
                      onClick={handleLogout}
                    >
                      <div className="flex items-center justify-center h-7 w-7 rounded-md bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
                        <LogOut className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                      </div>
                      <span className="font-medium text-sm">
                        {t("auth.logout")}
                      </span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {/* Desktop Auth Buttons */}
              <div className="hidden sm:flex items-center gap-2">
                <Button asChild variant="ghost" className="text-sm">
                  <Link to="/login">{t("auth.signIn")}</Link>
                </Button>
                <Button asChild className="text-sm">
                  <Link to="/register">{t("auth.signUp")}</Link>
                </Button>
              </div>

              {/* Mobile Unified Menu for non-authenticated users */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="sm:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 rounded-full border transition-all duration-200 hover:bg-accent"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 border shadow-2xl shadow-black/10 dark:shadow-white/5 rounded-xl mr-2"
                  align="end"
                  forceMount
                  sideOffset={8}
                  style={getDropdownGlassStyles()}
                >
                  {/* Authentication Links */}
                  <DropdownMenuGroup className="p-1">
                    <DropdownMenuItem asChild>
                      <Link
                        to="/login"
                        className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-950/20 group"
                      >
                        <div className="flex items-center justify-center h-7 w-7 rounded-md bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                          <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-medium text-sm">
                          {t("auth.signIn")}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/register"
                        className="cursor-pointer flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-green-50 dark:hover:bg-green-950/20 group"
                      >
                        <div className="flex items-center justify-center h-7 w-7 rounded-md bg-green-100 dark:bg-green-900/30 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                          <User className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="font-medium text-sm">
                          {t("auth.signUp")}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  <DropdownMenuSeparator />

                  {/* Theme & Language Settings */}
                  <div className="p-2">
                    <div className="flex items-center justify-between px-2 py-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("dashboard.appearance")}
                      </span>
                      <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <LanguageToggle />
                      </div>
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
